import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Check, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  accountantCompleteObligation,
  getAccountantIvaPeriodDetail,
} from "@/services/dashboardService";

/** "1.234,56" (UY) → 1234.56. Returns NaN for unparseable input. */
function parseUyAmount(raw: string): number {
  return parseFloat(raw.replace(/\./g, "").replace(",", "."));
}

function formatUyAmount(value: number): string {
  return value.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Per-obligation "Marcar pagada" action in the cartera's "Próximos
 * vencimientos" table. Completing flips the obligation to status=completed
 * (stops the agenda reminders) and drops it from the upcoming list.
 *
 * - BPS: no amount to enter — a single confirm.
 * - IVA (DGI): the amount input is pre-filled with the period's IVA a pagar
 *   (fetched on open), editable before saving.
 * - Other DGI taxes: a plain editable amount (no suggestion).
 */
export function AccountantObligationPaidControl({
  tenantId,
  obligationId,
  taxType,
  period,
  suggestedAmountUyu,
}: {
  tenantId: string;
  obligationId: string;
  taxType: string;
  period: string;
  /** Pre-computed owed amount (e.g. IRAE/IP saldo from the cierre). Prefills
   *  the input directly; IVA without one falls back to the period fetch. */
  suggestedAmountUyu?: number | null;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [prefilled, setPrefilled] = useState(false);

  const isBps = taxType === "BPS";
  const hasSuggested = suggestedAmountUyu != null;
  // Only fetch the IVA period when there's no precomputed amount to prefill.
  const isIva = taxType === "IVA" && !hasSuggested;

  // Pre-fill the IVA owed for the period — only fetched while the popover is
  // open on an IVA row without a precomputed amount.
  const ivaPeriodQuery = useQuery({
    queryKey: ["accountant", "tenant-iva-period", tenantId, period],
    queryFn: () => getAccountantIvaPeriodDetail(tenantId, period),
    enabled: open && isIva,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (open && !prefilled && !isBps) {
      if (hasSuggested) {
        setAmount(formatUyAmount(suggestedAmountUyu as number));
        setPrefilled(true);
      } else if (isIva && ivaPeriodQuery.data) {
        setAmount(formatUyAmount(ivaPeriodQuery.data.aPagarUyu));
        setPrefilled(true);
      }
    }
  }, [
    open,
    isBps,
    isIva,
    prefilled,
    hasSuggested,
    suggestedAmountUyu,
    ivaPeriodQuery.data,
  ]);

  const complete = useMutation({
    mutationFn: () => {
      let value: number | null = null;
      if (!isBps && amount.trim()) {
        const parsed = parseUyAmount(amount);
        if (!Number.isFinite(parsed) || parsed < 0) {
          throw new Error(
            t("accountantClientDetail.obligations.paid.invalid", "Monto inválido"),
          );
        }
        value = parsed;
      }
      return accountantCompleteObligation(tenantId, obligationId, value);
    },
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "tenant-detail", tenantId],
      });
      toast.success(
        t(
          "accountantClientDetail.obligations.paid.success",
          "Obligación marcada como pagada",
        ),
      );
    },
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : t("accountantClientDetail.obligations.paid.error", "No pudimos guardar"),
      );
    },
  });

  const onOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) {
      setAmount("");
      setPrefilled(false);
    }
  };

  const ivaLoading = isIva && open && ivaPeriodQuery.isLoading;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 border-[var(--fx-accent-300)] text-[var(--fx-accent-500)] hover:bg-[var(--fx-accent-100)]"
        >
          <Check className="h-3.5 w-3.5" />
          {t("accountantClientDetail.obligations.paid.mark", "Marcar pagada")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3">
        {isBps ? (
          <p className="text-sm text-muted-foreground">
            {t(
              "accountantClientDetail.obligations.paid.bpsConfirm",
              "¿Marcar esta obligación de BPS como pagada?",
            )}
          </p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor={`paid-${obligationId}`}>
              {t(
                "accountantClientDetail.obligations.paid.amountLabel",
                "Monto pagado (UYU)",
              )}
            </Label>
            <Input
              id={`paid-${obligationId}`}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={ivaLoading ? "…" : "0,00"}
              disabled={ivaLoading}
            />
            {isIva ? (
              <p className="text-xs text-muted-foreground">
                {ivaLoading
                  ? t(
                      "accountantClientDetail.obligations.paid.ivaLoading",
                      "Calculando IVA del período…",
                    )
                  : t(
                      "accountantClientDetail.obligations.paid.ivaHint",
                      "Pre-cargado con el IVA del período.",
                    )}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={complete.isPending}
          >
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => complete.mutate()}
            disabled={complete.isPending || ivaLoading}
          >
            {complete.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : null}
            {t("accountantClientDetail.obligations.paid.save", "Guardar")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
